import type { useTranslate } from "@refinedev/core";
import type { StatusTone } from "@fawkes/ui";

// communicatie (read-foundation UI): shared label/tone mapping for the enum-backed string codes the
// api passes through verbatim (the SPA owns the labels). A code with no i18n key falls back to the raw
// code so an unknown/new vocabulary value still renders legibly (never an empty cell).

type Translate = ReturnType<typeof useTranslate>;

/**
 * Status badge tone for the real CommunicationStatus enum (CONCEPT/ACTIEF/INACTIEF/FOUT):
 * ACTIEF=green, CONCEPT=orange, INACTIEF=neutral, FOUT=red. Unknown → neutral.
 */
export const TEMPLATE_STATUS_TONE: Record<string, StatusTone> = {
  ACTIEF: "success",
  CONCEPT: "warning",
  INACTIEF: "neutral",
  FOUT: "danger",
};

/**
 * communicatie status/herkomst badge colors — the EXACT Jmix theme hex (parity, not the generic
 * @fawkes/ui semantic tones). Sourced verbatim from the Jmix backoffice theme
 * `view/fawkes/communicatie/communicatie-module.css` (`.comm-status-badge--*` / `.comm-origin-badge--*`),
 * following the established "category colours = Jmix hex" pattern. An unknown/new code falls back to a
 * neutral grey so a row still renders legibly.
 */
export interface BadgeColors {
  bg: string;
  fg: string;
}

const NEUTRAL_BADGE: BadgeColors = { bg: "#eceef1", fg: "#5c6470" };

/** Status badge hex per CommunicationStatus (ACTIEF/CONCEPT/INACTIEF/FOUT). */
export const TEMPLATE_STATUS_COLORS: Record<string, BadgeColors> = {
  ACTIEF: { bg: "#e3f5e9", fg: "#1f7a43" },
  CONCEPT: { bg: "#e6eefc", fg: "#2b5fb3" },
  INACTIEF: { bg: "#eceef1", fg: "#5c6470" },
  FOUT: { bg: "#fde8e8", fg: "#b42318" },
};

/** Origin/herkomst badge hex per CommunicationOrigin (STANDAARD/AANGEPAST). */
export const TEMPLATE_ORIGIN_COLORS: Record<string, BadgeColors> = {
  STANDAARD: { bg: "#eaf7ee", fg: "#2e7d4f" },
  AANGEPAST: { bg: "#f0e9fb", fg: "#6b3fa0" },
};

export function statusBadgeColors(code: string | null | undefined): BadgeColors {
  return (code && TEMPLATE_STATUS_COLORS[code]) || NEUTRAL_BADGE;
}

export function originBadgeColors(code: string | null | undefined): BadgeColors {
  return (code && TEMPLATE_ORIGIN_COLORS[code]) || NEUTRAL_BADGE;
}

/** Log status tone — VERZONDEN/AFGEDRUKT=green, CONCEPT=orange, MISLUKT=red. Unknown → neutral. */
export const LOG_STATUS_TONE: Record<string, StatusTone> = {
  VERZONDEN: "success",
  AFGEDRUKT: "success",
  CONCEPT: "warning",
  MISLUKT: "danger",
};

/** Resolve `communicatieRead.type.<code>`, falling back to the raw code. */
export function typeLabel(code: string | null | undefined, t: Translate): string {
  if (!code) return "—";
  return t(`communicatieRead.type.${code}`, undefined, code);
}

export function statusLabel(code: string | null | undefined, t: Translate): string {
  if (!code) return "—";
  return t(`communicatieRead.status.${code}`, undefined, code);
}

/**
 * Resolve `communicatieRead.category.<code>` for the CommunicationCategory enum
 * (ASIEL/PENSION/FACTUREN/PLANNING/DIER/RELATIE/ALGEMEEN). The api passes the raw
 * uppercase code through verbatim; Jmix renders the enum's localized label, so the SPA
 * must humanize it the same way (e.g. ASIEL → "Asiel"). Unknown code → the raw code.
 */
export function categoryLabel(code: string | null | undefined, t: Translate): string {
  if (!code) return "—";
  return t(`communicatieRead.category.${code}`, undefined, code);
}

export function originLabel(code: string | null | undefined, t: Translate): string {
  if (!code) return "—";
  return t(`communicatieRead.origin.${code}`, undefined, code);
}

export function logTypeLabel(code: string | null | undefined, t: Translate): string {
  if (!code) return "—";
  return t(`communicatieRead.logType.${code}`, undefined, code);
}

export function logStatusLabel(code: string | null | undefined, t: Translate): string {
  if (!code) return "—";
  return t(`communicatieRead.logStatus.${code}`, undefined, code);
}

export function mediaTypeLabel(code: string | null | undefined, t: Translate): string {
  if (!code) return "—";
  return t(`communicatieRead.mediaType.${code}`, undefined, code);
}

export function mediaUsageLabel(code: string | null | undefined, t: Translate): string {
  if (!code) return "—";
  return t(`communicatieRead.mediaUsage.${code}`, undefined, code);
}
